// src/components/layout/DashboardLayout.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { logout } from '../../store/slices/authSlice';
import { fetchNotifications } from '../../store/slices/notificationsSlice';
import Logo from '../common/Logo';
import NotificationCenter from '../common/NotificationCenter';
import ConversationList from '../common/ConversationList';
import { 
  DashboardIcon, SearchIcon, CalendarIcon, PersonIcon, 
  StoreIcon, ScheduleIcon, StarIcon, TimeIcon,
  LogoutIcon, MenuIcon, NotificationsIcon, ChatIcon, CloseIcon
} from '../common/Icons';
import StoreDownloadCta from '../common/StoreDownloadCta';
import '../../styles/MobileAppPromo.scss';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';

interface DashboardLayoutProps {
  userType?: 'client' | 'stylist' | 'admin';
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ userType = 'client' }) => {
  const { stylistApproval: isStylistApprovalEnabled } = useFeatureFlags();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const unreadNotifications = useAppSelector((state) => state.notifications.unreadCount);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Initialize drawer: open on desktop, closed on mobile
  const [drawerOpen, setDrawerOpen] = useState(() => window.innerWidth >= 960);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);
  const [mobileAppModalOpen, setMobileAppModalOpen] = useState(false);
  const [selectedMobileFeature, setSelectedMobileFeature] = useState('');
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 960;
      setIsMobile(mobile);
      // On desktop, always show sidebar. On mobile, keep current state (don't force close)
      if (!mobile) {
        setDrawerOpen(true);
      }
      // On mobile, don't force close - let user control it via toggle
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Unread notification count: Redux (same source as notification panel + mark-read API)
  useEffect(() => {
    if (!user?.id) return;
    dispatch(fetchNotifications({ limit: 30, page: 1 }));
    const intervalId = setInterval(() => {
      dispatch(fetchNotifications({ limit: 30, page: 1 }));
    }, 30000);
    return () => clearInterval(intervalId);
  }, [user?.id, dispatch]);

  // Fetch chat unread count
  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout;

    const fetchCounts = async () => {
      try {
        const { getTotalUnreadCount } = await import('../../services/chatService');
        const unreadChatCount = await getTotalUnreadCount();

        if (isMounted) {
          setUnreadMessages(unreadChatCount);
        }
      } catch (error) {
        console.error('Error fetching chat counts:', error);
        if (isMounted) {
          setUnreadMessages(0);
        }
      }
    };

    fetchCounts();
    intervalId = setInterval(fetchCounts, 30000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);
  
  // Generate navigation items based on user type
  const getNavigationItems = () => {
    if (userType === 'client') {
      return [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/client/dashboard', available: true },
        { text: 'Browse Stylists', icon: <SearchIcon />, path: '/client/browse', available: true },
        { text: 'My Appointments', icon: <CalendarIcon />, path: '/client/appointments', available: true },
        { text: 'My Profile', icon: <PersonIcon />, path: '/client/profile', available: true },
      ];
    } else if (userType === 'stylist') {
      return [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/stylist/dashboard', available: true },
        { text: 'My Services', icon: <StoreIcon />, path: '/stylist/service-management', available: true },
        { text: 'Schedule & Availability', icon: <ScheduleIcon />, path: '/stylist/availability', available: true },
        { text: 'Appointments', icon: <CalendarIcon />, path: '/stylist/appointments', available: true },
        { text: 'My Promotions', icon: <StoreIcon />, path: '/stylist/promotions', available: true },
        { text: 'Reviews', icon: <StarIcon />, path: '/stylist/reviews', available: false, mobileOnly: true },
        { text: 'Portfolio', icon: <StarIcon />, path: '/stylist/portfolio', available: false, mobileOnly: true },
        { text: 'Analytics', icon: <StarIcon />, path: '/stylist/analytics', available: false, mobileOnly: true },
        { text: 'My Profile', icon: <PersonIcon />, path: '/stylist/profile', available: true },
      ];
    } else {
      // Check if user is a support agent
      const ar = (user as { adminRole?: string })?.adminRole;
      const isSupportAgent =
        user?.userType === 'admin' &&
        (ar === 'supportAgent' || ar === 'super_admin' || ar === 'superAdmin');
      
      const stylistApprovalNavItem = isStylistApprovalEnabled
        ? [{ text: 'Stylist Approval', icon: <PersonIcon />, path: '/admin/stylist-approval', available: true }]
        : [];

      const adminExtras = [
        { text: 'Quick User Search', icon: <SearchIcon />, path: '/admin/quick-user-search', available: true },
        { text: 'Feature Gates', icon: <TimeIcon />, path: '/admin/feature-gates', available: true },
        { text: 'System Health', icon: <TimeIcon />, path: '/admin/system-health', available: true },
        { text: 'Action Center', icon: <DashboardIcon />, path: '/admin/action-center', available: true },
      ];

      if (isSupportAgent) {
        return [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', available: true },
          { text: 'Audit Trails', icon: <TimeIcon />, path: '/admin/audit-trails', available: true },
          { text: 'Support Chat', icon: <ChatIcon />, path: '/admin/support', available: true },
          { text: 'Email Verification', icon: <PersonIcon />, path: '/admin/email-verification', available: true },
          ...stylistApprovalNavItem,
          { text: 'Referral Agents', icon: <StoreIcon />, path: '/admin/affiliates', available: true },
          { text: 'Payouts', icon: <StoreIcon />, path: '/admin/payouts', available: true },
          { text: 'Disputes', icon: <StoreIcon />, path: '/admin/disputes', available: true },
          ...adminExtras,
          // Only roles the Account Deletions route and API admit.
          { text: 'Account Deletions', icon: <PersonIcon />, path: '/admin/account-deletions', available: true },
        ];
      } else {
        return [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', available: true },
          { text: 'Audit Trails', icon: <TimeIcon />, path: '/admin/audit-trails', available: true },
          { text: 'Users', icon: <PersonIcon />, path: '/admin/users', available: true },
          { text: 'Services', icon: <StoreIcon />, path: '/admin/services', available: true },
          { text: 'Email Verification', icon: <PersonIcon />, path: '/admin/email-verification', available: true },
          ...stylistApprovalNavItem,
          { text: 'Referral Agents', icon: <StoreIcon />, path: '/admin/affiliates', available: true },
          { text: 'Payouts', icon: <StoreIcon />, path: '/admin/payouts', available: true },
          { text: 'Disputes', icon: <StoreIcon />, path: '/admin/disputes', available: true },
          ...adminExtras,
        ];
      }
    }
  };

  const navigationItems = getNavigationItems();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuToggle = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const handleNotificationsToggle = () => {
    const next = !notificationsOpen;
    setNotificationsOpen(next);
    if (next && user?.id) {
      dispatch(fetchNotifications({ limit: 30, page: 1 }));
    }
  };
  
  const handleChatToggle = () => {
    setChatOpen(!chatOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    // Signing out returns to the public marketplace, not a login wall
    // (same as mobile; App Store 5.1.1(v) guest discovery).
    navigate('/browse');
  };

  const handleMobileOnlyFeature = (featureName: string) => {
    setSelectedMobileFeature(featureName);
    setMobileAppModalOpen(true);
  };

  return (
    <div className="dashboard-layout">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside className={`dashboard-sidebar ${drawerOpen ? 'open' : ''}`}>
          <div className="dashboard-sidebar__header">
            <Logo />
          </div>
          
          <nav className="dashboard-sidebar__nav">
            {navigationItems.map((item) => (
              item.available ? (
                <Link
                  key={item.text}
                  to={item.path}
                  className={`dashboard-sidebar__nav-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="dashboard-sidebar__nav-icon">{item.icon}</span>
                  <span className="dashboard-sidebar__nav-text">{item.text}</span>
                </Link>
              ) : (
                <button
                  key={item.text}
                  onClick={() => handleMobileOnlyFeature(item.text)}
                  className="dashboard-sidebar__nav-item dashboard-sidebar__nav-item--disabled"
                  title="Available on mobile app"
                >
                  <span className="dashboard-sidebar__nav-icon">{item.icon}</span>
                  <span className="dashboard-sidebar__nav-text">
                    {item.text}
                    <span className="mobile-only-badge">📱 Mobile</span>
                  </span>
                </button>
              )
            ))}
          </nav>
          
          {userType === 'stylist' && (
            <div className="dashboard-sidebar__promo">
              <div className="mobile-app-promo-card">
                <div className="mobile-app-promo-icon">📱</div>
                <h3>Get the Full Experience</h3>
                <p>Download our mobile app for access to all features including reviews, portfolio management, and analytics.</p>
                <button className="btn btn--primary btn--sm" onClick={() => setMobileAppModalOpen(true)}>
                  Learn More
                </button>
              </div>
            </div>
          )}
        </aside>
      )}

      {/* Main content area */}
      <main className={`dashboard-main ${drawerOpen && !isMobile ? 'sidebar-open' : ''}`}>
        {/* Top header/navbar */}
        <header className="dashboard-header">
          <div className="dashboard-header__left">
            <div className="dashboard-header__menu-wrapper" style={{ position: 'relative', zIndex: 1500 }}>
              <button 
                className="dashboard-header__menu-button"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </button>
              {/* Mobile Dropdown Menu - positioned relative to menu button */}
              {isMobile && drawerOpen && (
                <div 
                  ref={menuRef}
                  className="dashboard-mobile-menu"
                  onClick={(e) => {
                    // Stop propagation to prevent overlay from closing menu
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <nav className="dashboard-mobile-menu__nav">
                    {navigationItems.map((item) => (
                      item.available ? (
                        <button
                          key={item.text}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDrawerOpen(false);
                            navigate(item.path);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          className={`dashboard-mobile-menu__item ${location.pathname === item.path ? 'active' : ''}`}
                        >
                          <span className="dashboard-mobile-menu__icon">{item.icon}</span>
                          <span className="dashboard-mobile-menu__text">{item.text}</span>
                        </button>
                      ) : (
                        <button
                          key={item.text}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleMobileOnlyFeature(item.text);
                            setDrawerOpen(false);
                          }}
                          className="dashboard-mobile-menu__item dashboard-mobile-menu__item--disabled"
                          title="Available on mobile app"
                        >
                          <span className="dashboard-mobile-menu__icon">{item.icon}</span>
                          <span className="dashboard-mobile-menu__text">
                            {item.text}
                            <span className="mobile-only-badge">📱 Mobile</span>
                          </span>
                        </button>
                      )
                    ))}
                  </nav>
                </div>
              )}
            </div>
            <h1 className="dashboard-header__title">
              {userType === 'client' ? 'Client Dashboard' : userType === 'stylist' ? 'Stylist Dashboard' : 'Admin Dashboard'}
            </h1>
          </div>

          <div className="dashboard-header__actions">
            {/* Chat button */}
            <button 
              className="dashboard-header__action-button"
              onClick={handleChatToggle}
            >
              <ChatIcon />
              {unreadMessages > 0 && (
                <span className="notification-badge">{unreadMessages}</span>
              )}
            </button>

            {/* Notifications button */}
            <button 
              className="dashboard-header__action-button"
              onClick={handleNotificationsToggle}
            >
              <NotificationsIcon />
              {unreadNotifications > 0 && (
                <span className="notification-badge">{unreadNotifications}</span>
              )}
            </button>

            {/* Profile button */}
            <div className="dashboard-header__profile">
              <button 
                className="dashboard-header__profile-button"
                onClick={handleProfileMenuToggle}
              >
                {user?.profilePhoto?.url ? (
                  <img 
                    src={user.profilePhoto.url} 
                    alt={user?.name || 'User'} 
                    className="dashboard-header__profile-avatar"
                  />
                ) : (
                  <div className="dashboard-header__profile-avatar-placeholder">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
              </button>

              {/* Profile dropdown menu */}
              {profileMenuOpen && (
                <div className="dashboard-profile-menu">
                  <Link 
                    to={`/${userType}/profile`}
                    className="dashboard-profile-menu__item"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      // Close drawer on mobile when clicking profile
                      if (isMobile) {
                        setDrawerOpen(false);
                      }
                    }}
                  >
                    <PersonIcon />
                    <span>Profile</span>
                  </Link>
                  <div className="dashboard-profile-menu__divider"></div>
                  <button 
                    className="dashboard-profile-menu__item"
                    onClick={() => {
                      handleLogout();
                      // Close drawer on mobile when logging out
                      if (isMobile) {
                        setDrawerOpen(false);
                      }
                    }}
                  >
                    <LogoutIcon />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <div className="dashboard-content">
          <div className="container">
            <Outlet />
          </div>
        </div>

        {/* Notification panel */}
        {notificationsOpen && (
          <div className="dashboard-panel notifications-panel">
            <div className="dashboard-panel__header">
              <h2 className="dashboard-panel__title">Notifications</h2>
              <button 
                className="dashboard-panel__close"
                onClick={() => setNotificationsOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="dashboard-panel__content">
              <NotificationCenter
                variant="embedded"
                userType={userType}
                onClose={() => setNotificationsOpen(false)}
                onViewAll={() => {
                  navigate(`/${userType}/notifications`);
                  setNotificationsOpen(false);
                }}
              />
            </div>
          </div>
        )}

        {/* Chat panel */}
        {chatOpen && (
          <div className="dashboard-panel chat-panel">
            <div className="dashboard-panel__header">
              <h2 className="dashboard-panel__title">Messages</h2>
              <button 
                className="dashboard-panel__close"
                onClick={() => setChatOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="dashboard-panel__content">
              <ConversationList 
                onClose={() => setChatOpen(false)}
                onSelectConversation={(conversationId) => {
                  navigate(`/${userType}/chat/${conversationId}`);
                  setChatOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </main>

      {/* Mobile overlay when drawer is open - disabled to prevent blocking menu clicks */}
      {false && isMobile && drawerOpen && (
        <div 
          className="dashboard-overlay"
          onClick={(e) => {
            // Only close if clicking directly on overlay (not on menu or its children)
            const target = e.target as HTMLElement;
            if (target.classList.contains('dashboard-overlay')) {
              // Check if click is outside the menu
              if (menuRef.current && !menuRef.current.contains(target)) {
                handleDrawerToggle();
              }
            }
          }}
        ></div>
      )}

      {/* Mobile App Promotion Modal */}
      {mobileAppModalOpen && (
        <div className="modal-overlay" onClick={() => setMobileAppModalOpen(false)}>
          <div className="modal-content mobile-app-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setMobileAppModalOpen(false)}>
              <CloseIcon />
            </button>
            
            <div className="mobile-app-modal__header">
              <div className="mobile-app-modal__icon">📱</div>
              <h2>Get the LookBel Mobile App</h2>
              <p>Experience the full power of LookBel on your mobile device</p>
            </div>

            <div className="mobile-app-modal__content">
              {selectedMobileFeature && (
                <div className="mobile-app-modal__feature-notice">
                  <strong>{selectedMobileFeature}</strong> is available on the mobile app
                </div>
              )}

              <h3>Mobile-Only Features:</h3>
              <ul className="mobile-app-modal__features">
                <li>
                  <span className="feature-icon">⭐</span>
                  <div>
                    <strong>Reviews & Ratings</strong>
                    <p>View and respond to client reviews</p>
                  </div>
                </li>
                <li>
                  <span className="feature-icon">🖼️</span>
                  <div>
                    <strong>Portfolio Management</strong>
                    <p>Showcase your work with photos</p>
                  </div>
                </li>
                <li>
                  <span className="feature-icon">📊</span>
                  <div>
                    <strong>Advanced Analytics</strong>
                    <p>Track your performance metrics</p>
                  </div>
                </li>
                <li>
                  <span className="feature-icon">💬</span>
                  <div>
                    <strong>Real-time Chat</strong>
                    <p>Communicate with clients instantly</p>
                  </div>
                </li>
                <li>
                  <span className="feature-icon">📍</span>
                  <div>
                    <strong>GPS Check-in</strong>
                    <p>For mobile service appointments</p>
                  </div>
                </li>
                <li>
                  <span className="feature-icon">🔔</span>
                  <div>
                    <strong>Push Notifications</strong>
                    <p>Never miss an appointment update</p>
                  </div>
                </li>
              </ul>

              <div className="mobile-app-modal__availability">
                <p><strong>Available on Web:</strong></p>
                <ul>
                  <li>✓ Service Management</li>
                  <li>✓ Schedule & Availability</li>
                  <li>✓ Appointment Management</li>
                  <li>✓ Profile Settings</li>
                </ul>
              </div>
            </div>

            <div className="mobile-app-modal__footer">
              <h3>Download Now</h3>
              <div className="mobile-app-modal__download-buttons">
                <StoreDownloadCta store="ios" variant="modal" />
                <StoreDownloadCta store="android" variant="modal" />
              </div>
              <p className="mobile-app-modal__note">
                Your account works seamlessly across web and mobile
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;